import React, { useEffect, useState, useRef } from 'react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { QRCodeSVG } from 'qrcode.react';
import { api } from '../../utils/api';

const MaintenanceReceiptPrint = ({ data }) => {
  // Estrai i dati dal prop data
  const maintenance = data?.maintenance;
  const paymentMethod = data?.paymentMethod;
  const account = data?.account;
  const paymentAmount = data?.paymentAmount;
  const paymentDate = data?.paymentDate;
  
  const [companyData, setCompanyData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const receiptRef = useRef();
  
  // Recupero i dati aziendali dalla tabella CompanySettings
  useEffect(() => {
    const fetchCompanySettings = async () => {
      try {
        setIsLoading(true);
        const companyId = localStorage.getItem('companyId');
        if (!companyId) {
          throw new Error('CompanyId not found in localStorage');
        }

        const response = await api.get('/company-settings', {
          headers: {
            'x-company-id': companyId
          }
        });
        
        // Mappa i dati dalle impostazioni al formato richiesto
        setCompanyData({
          name: response.data.companyName || 'FreeWheel',
          address: response.data.companyAddress || '',
          phone: response.data.companyPhone || '',
          city: response.data.companyCity || '',
          postalCode: response.data.companyPostalCode || '',
          province: response.data.companyProvince || '',
          email: response.data.companyEmail || '',
          vatNumber: response.data.companyVatNumber || ''
        });
      } catch (error) {
        console.error('Errore nel caricamento dei dati aziendali:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCompanySettings();
  }, []);

  // Creo l'URL con i parametri per il QR code
  const trackingUrl = new URL('https://freewheel-backend.onrender.com/track-maintenance');
  trackingUrl.searchParams.append('protocol', maintenance?.protocolNumber || '');
  trackingUrl.searchParams.append('phone', maintenance?.customerPhone || '');

  // Funzione per formattare gli importi
  const formatCurrency = (amount) => {
    return parseFloat(amount).toFixed(2);
  };

  // --- PAGINAZIONE ---
  // Calcola quante righe ci stanno in una pagina (stimato per A5, font 9pt)
  const ROWS_PER_PAGE = 16; // puoi regolare questo valore per il tuo layout
  const items = maintenance?.MaintenanceItems || [];
  const totalPages = Math.ceil(items.length / ROWS_PER_PAGE) || 1;

  // Split items in pagine
  const pagedItems = [];
  for (let i = 0; i < totalPages; i++) {
    pagedItems.push(items.slice(i * ROWS_PER_PAGE, (i + 1) * ROWS_PER_PAGE));
  }

  if (isLoading) {
    return <div>Caricamento dati aziendali...</div>;
  }

  return (
    <div id="receipt-print" ref={receiptRef} style={{
      width: '148mm',
      height: '210mm',
      margin: '0 auto',
      padding: '10mm',
      fontFamily: 'Helvetica, Arial, sans-serif',
      fontSize: '9pt',
      boxSizing: 'border-box',
      backgroundColor: 'white',
      color: '#333',
      position: 'relative'
    }}>
      {pagedItems.map((pageItems, pageIndex) => (
        <div key={pageIndex} className="receipt-page" style={{
          width: '100%',
          height: '190mm',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          position: 'relative',
          marginBottom: pageIndex < totalPages - 1 ? '10mm' : 0
        }}>
          {/* Header con info aziendali e logo */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '1.5mm', // ridotto del 50%
            maxWidth: '100%'
          }}>
            <div style={{ maxWidth: '65%' }}>
              <div style={{ 
                fontSize: '14pt', 
                fontWeight: 'bold',
                color: '#DB1111',
                marginBottom: '1mm', // ridotto
                wordBreak: 'break-word'
              }}>
                {companyData?.name}
              </div>
              <div style={{ 
                fontSize: '8pt', 
                color: '#666',
                lineHeight: '1.1' // più compatto
              }}>
                <div style={{ wordBreak: 'break-word' }}>{companyData?.address}</div>
                <div>{companyData?.city} {companyData?.postalCode} {companyData?.province}</div>
                <div>Tel: {companyData?.phone}</div>
                <div>Email: {companyData?.email}</div>
                {companyData?.vatNumber && <div>P.IVA: {companyData?.vatNumber}</div>}
              </div>
            </div>
            <div style={{
              backgroundColor: '#f8f8f8',
              padding: '2mm',
              borderRadius: '2px',
              border: '1px solid #eee',
              minWidth: '40mm',
              textAlign: 'center',
              position: 'relative'
            }}>
              <div style={{ fontSize: '7pt', color: '#666' }}>
                Manutenzione N.:
              </div>
              <div style={{ fontSize: '12pt', fontWeight: 'bold', color: '#DB1111' }}>
                #{maintenance?.protocolNumber}
              </div>
              <div style={{ marginTop: '2mm' }}>
                <QRCodeSVG 
                  value={trackingUrl.toString()}
                  size={50}
                  level="M"
                  includeMargin={false}
                />
              </div>
            </div>
          </div>

          {/* Titolo Documento */}
          <div style={{
            textAlign: 'center',
            fontSize: '13pt', // leggermente più piccolo
            fontWeight: 'bold',
            margin: '1.5mm 0', // ridotto del 50%
            color: '#333',
            borderBottom: '2px solid #DB1111',
            paddingBottom: '0.5mm' // ridotto del 50%
          }}>
            RICEVUTA DI PAGAMENTO
          </div>

          {/* Info Cliente e Data */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '1.5mm', // ridotto
            marginBottom: '1.5mm', // ridotto del 50%
            backgroundColor: '#f8f8f8',
            padding: '1.5mm', // ridotto
            borderRadius: '2px'
          }}>
            <div>
              <div style={{ fontSize: '8pt', color: '#666', marginBottom: '0.5mm' }}>Cliente</div>
              <div style={{ fontWeight: 'bold', fontSize: '9pt' }}>{maintenance?.customerName}</div>
            </div>
            <div>
              <div style={{ fontSize: '8pt', color: '#666', marginBottom: '0.5mm' }}>Telefono</div>
              <div style={{ fontWeight: 'bold', fontSize: '9pt' }}>{maintenance?.customerPhone}</div>
            </div>
            <div>
              <div style={{ fontSize: '8pt', color: '#666', marginBottom: '0.5mm' }}>Data Pagamento</div>
              <div style={{ fontWeight: 'bold', fontSize: '9pt' }}>
                {paymentDate ? format(new Date(paymentDate), 'dd/MM/yyyy', { locale: it }) : format(new Date(), 'dd/MM/yyyy', { locale: it })}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '8pt', color: '#666', marginBottom: '0.5mm' }}>Numero Ricevuta</div>
              <div style={{ fontWeight: 'bold', fontSize: '9pt' }}>RIC-{maintenance?.protocolNumber}</div>
            </div>
          </div>

          {/* Dettagli Veicolo */}
          <div style={{
            marginBottom: '1.5mm', // ridotto del 50%
            border: '1px solid #eee',
            borderRadius: '2px',
            padding: '1.5mm' // ridotto
          }}>
            <div style={{ 
              fontWeight: 'bold', 
              marginBottom: '1mm', // ridotto
              color: '#DB1111',
              fontSize: '10pt'
            }}>
              DETTAGLI VEICOLO
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5mm' }}>
              <div>
                <div style={{ fontSize: '8pt', color: '#666', marginBottom: '0.5mm' }}>Marca</div>
                <div style={{ fontWeight: 'bold', fontSize: '9pt' }}>{maintenance?.bikeBrand}</div>
              </div>
              <div>
                <div style={{ fontSize: '8pt', color: '#666', marginBottom: '0.5mm' }}>Modello</div>
                <div style={{ fontWeight: 'bold', fontSize: '9pt' }}>{maintenance?.bikeModel}</div>
              </div>
            </div>
          </div>

          {/* Lavori e Prodotti utilizzati */}
          {pageItems.length > 0 && (
            <div style={{ marginBottom: '4mm', flex: 1 }}>
              <div style={{
                fontWeight: 'bold',
                marginBottom: '1mm',
                color: '#DB1111',
                fontSize: '10pt'
              }}>
                LAVORI E PRODOTTI UTILIZZATI
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9pt' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '1mm 0.5mm' }}>Descrizione</th>
                    <th style={{ textAlign: 'right', padding: '1mm 0.5mm' }}>Quantità</th>
                    <th style={{ textAlign: 'right', padding: '1mm 0.5mm' }}>Prezzo</th>
                    <th style={{ textAlign: 'right', padding: '1mm 0.5mm' }}>Totale</th>
                  </tr>
                </thead>
                <tbody>
                  {pageItems.map(item => (
                    <tr key={item.id}>
                      <td style={{ padding: '0.5mm' }}>
                        {item.isLabor
                          ? item.description || 'Manodopera'
                          : (item.customItem
                              ? item.customDescription
                              : (item.Product?.name || 'Prodotto'))}
                      </td>
                      <td style={{ textAlign: 'right', padding: '0.5mm' }}>{item.quantity}</td>
                      <td style={{ textAlign: 'right', padding: '0.5mm' }}>€{parseFloat(item.unitPrice).toFixed(2)}</td>
                      <td style={{ textAlign: 'right', padding: '0.5mm' }}>€{(item.quantity * item.unitPrice).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Dettagli Pagamento SOLO nell'ultima pagina */}
          {pageIndex === totalPages - 1 && (
            <div style={{ marginBottom: '3mm' }}>
              <div style={{ 
                fontWeight: 'bold',
                marginBottom: '0.5mm',
                color: '#DB1111',
                fontSize: '10pt'
              }}>
                DETTAGLI PAGAMENTO
              </div>
              <div style={{ 
                padding: '1.5mm',
                border: '1px solid #eee',
                borderRadius: '2px',
                backgroundColor: '#f8f8f8'
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9pt' }}>
                  <tbody>
                    <tr>
                      <td style={{ padding: '0.5mm 1mm', borderBottom: '1px solid #eee' }}>Totale intervento</td>
                      <td style={{ padding: '0.5mm 1mm', textAlign: 'right', fontWeight: 'bold', borderBottom: '1px solid #eee' }}>
                        €{formatCurrency(maintenance?.finalCost || 0)}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: '0.5mm 1mm', borderBottom: '1px solid #eee' }}>Acconto versato</td>
                      <td style={{ padding: '0.5mm 1mm', textAlign: 'right', borderBottom: '1px solid #eee' }}>
                        €{formatCurrency(maintenance?.deposit || 0)}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: '0.5mm 1mm', borderBottom: '1px solid #eee' }}>Saldo versato</td>
                      <td style={{ padding: '0.5mm 1mm', textAlign: 'right', borderBottom: '1px solid #eee' }}>
                        €{formatCurrency(paymentAmount || 0)}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: '0.5mm 1mm', borderBottom: '1px solid #eee' }}>Metodo di pagamento</td>
                      <td style={{ padding: '0.5mm 1mm', textAlign: 'right', borderBottom: '1px solid #eee' }}>
                        {paymentMethod?.name || ''}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div style={{ flex: 1 }} /> {/* Spinge il footer in basso */}

          {/* Numero pagina in basso a destra, su tutte le pagine */}
          <div
            style={{
              position: 'absolute',
              bottom: '1mm',
              right: '10mm',
              fontSize: '8pt',
              color: '#888'
            }}
          >
            Pagina {pageIndex + 1} / {totalPages}
          </div>

          {/* Messaggio di ringraziamento in calce */}
          {pageIndex === totalPages - 1 && (
            <div style={{
              width: '100%',
              textAlign: 'center',
              marginTop: '0mm',
              fontSize: '10pt',
              color: '#333',
              fontWeight: 'bold'
            }}>
              Grazie per aver scelto i nostri servizi!<br />
              Lo staff di {companyData?.name}
            </div>
          )}
        </div>
      ))}
      {/* Nessun CSS di page-break, nessun overflow:hidden */}
    </div>
  );
};

export default MaintenanceReceiptPrint; 